import { useCallback, useEffect, useState } from "react";
import { Lock, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { wmCode, wmMessage } from "@/lib/wmError";
import { countsAsWaste } from "@/components/waste-meals/wmUtils";

const C = OPS.wm.admin;
const EMPTY = { key: "", name_ar: "", name_en: "", counts_as_waste: true };

/** The API says the reason is in use: then `counts_as_waste` is frozen
 *  (WM_REASON_HAS_RECORDS) — flipping it would rewrite history, because
 *  returns are classified when the totals are read. */
const inUse = (r) => (Number(r.records_count) || 0) > 0 || r.has_records === true || r.counts_as_waste_locked === true;

/** «أسباب الهدر» — the spec requires them to be manageable, not hard-coded:
 *  انتهاء صلاحية · تلف · حرق · خطأ تحضير · زيادة إنتاج · مرتجع عميل …
 *
 *  2026-10-16 (D2): «يُحتسب هدراً» per reason. Off = a stock movement such as
 *  «مرتجع للمستودع»: still recorded and visible, left out of waste totals,
 *  waste % and cost. Settable on create; locked once any record uses it. */
export function ReasonsAdmin() {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState(EMPTY);
    const [lockedHere, setLockedHere] = useState(() => new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/waste-meals/reasons");
            setRows(r.data.rows || []);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [lang, t]);

    useEffect(() => { load(); }, [load]);

    const call = async (fn, onError) => {
        setBusy(true);
        try {
            await fn();
            await load();
        } catch (e) {
            if (onError) onError(e);
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    const add = () => {
        if (!draft.key.trim() || !draft.name_ar.trim()) {
            toast.error(t("wm_admin_reason_required"));
            return;
        }
        call(async () => {
            await api.post("/waste-meals/reasons", draft);
            setDraft(EMPTY);
            toast.success(t("wm_saved"));
        });
    };

    const setCounts = (r, v) => call(
        async () => { await api.patch(`/waste-meals/reasons/${r.key}`, { counts_as_waste: v }); },
        // the list did not say so, the server did: lock the switch from now on
        (e) => { if (wmCode(e) === "WM_REASON_HAS_RECORDS") setLockedHere((s) => new Set(s).add(r.key)); },
    );

    if (loading) return <InlineLoader />;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <Input data-testid={C.reasonKey} value={draft.key} dir="ltr" placeholder={t("wm_admin_reason_key")}
                       onChange={(e) => setDraft({ ...draft, key: e.target.value })} className="h-9 w-36" />
                <Input data-testid={C.reasonNameAr} value={draft.name_ar} dir="rtl" placeholder={t("wm_admin_name_ar")}
                       onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} className="h-9 w-40" />
                <Input data-testid={C.reasonNameEn} value={draft.name_en} dir="ltr" placeholder={t("wm_admin_name_en")}
                       onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} className="h-9 w-40" />
                <label className="flex h-9 items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] px-2.5 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <Switch data-testid={C.reasonNewCountsAsWaste} checked={draft.counts_as_waste}
                            onCheckedChange={(v) => setDraft({ ...draft, counts_as_waste: v })} />
                    {t("wm_admin_counts_as_waste")}
                </label>
                <Button data-testid={C.reasonAdd} size="sm" onClick={add} disabled={busy}
                        className="h-9 gap-1.5 text-xs font-semibold">
                    <Plus className="h-4 w-4" />
                    {t("wm_admin_reason_add")}
                </Button>
                {!draft.counts_as_waste ? (
                    <p className="w-full text-[11px] leading-relaxed text-[color:var(--brand-brown)]">
                        {t("wm_admin_counts_off_hint")}
                    </p>
                ) : null}
            </div>

            <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]">
                <table data-testid={C.reasonsTable} className="w-full text-sm">
                    <thead className="bg-[color:var(--bg-surface-2)]">
                        <tr>
                            {[["wm_admin_reason_key"], ["wm_admin_name_ar"], ["wm_admin_name_en", true],
                              ["wm_admin_counts_as_waste"], ["wm_admin_state"]].map(([h, wide]) => (
                                <th key={h} className={`px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)] ${
                                    wide ? "hidden sm:table-cell" : ""}`}>{t(h)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const locked = inUse(r) || lockedHere.has(r.key);
                            const counts = countsAsWaste(r);
                            return (
                                <tr key={r.key} data-testid={C.reasonRow(r.key)} className="border-t border-[color:var(--separator)]">
                                    <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]"><bdi dir="ltr">{r.key}</bdi></td>
                                    <td className="px-3 py-2.5 font-semibold text-[color:var(--ink)]">
                                        {r.name_ar}
                                        {!counts ? (
                                            <span className="block text-[10px] font-semibold text-[color:var(--brand-brown)]">
                                                {t("wm_reason_not_waste")}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="hidden px-3 py-2.5 text-xs text-[color:var(--ink-muted)] sm:table-cell">{r.name_en || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <Switch data-testid={C.reasonCountsAsWaste(r.key)} checked={counts}
                                                disabled={busy || locked} aria-label={t("wm_admin_counts_as_waste")}
                                                onCheckedChange={(v) => setCounts(r, v)} />
                                        {locked ? (
                                            <span data-testid={C.reasonLocked(r.key)}
                                                  className="mt-1 flex items-center gap-1 text-[10px] leading-snug text-[color:var(--ink-faint)]">
                                                <Lock className="h-3 w-3 shrink-0" />
                                                {t("wm_admin_counts_locked")}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Switch data-testid={C.reasonToggle(r.key)} checked={r.is_active !== false} disabled={busy}
                                                onCheckedChange={(v) => call(async () => {
                                                    await api.patch(`/waste-meals/reasons/${r.key}`, { is_active: v });
                                                })} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
